import type { IComponentController, IScope } from "angular";
import { finalize } from "rxjs/operators";

import { Subscription } from "rxjs";

import { cloneDeep, isEmpty, isEqual } from "lodash";

import type { IEquipmentFunctionalityType, IResponseWithLoading, IrMap, ResolutionMode } from "App/Modules/Models";
import { FpsFunctions, ResolutionModeFunctions } from "App/Modules/Models";
import type { IEquipmentFunctionalityTypesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditVideoPropertiesComponentController implements IComponentController {
    public static $inject: string[] = ["$scope", "irEquipmentFunctionalityTypesRepository"];

    public resolve: IFormContainerResolve<IEquipmentFunctionalityType, void>;

    public editResponse: IResponseWithLoading<IEquipmentFunctionalityType>;

    public equipmentFunctionalityType: IEquipmentFunctionalityType;

    public originalEquipmentFunctionalityType: IEquipmentFunctionalityType;

    public fps: IResponseWithLoading<IrMap<number, string>>;

    public resolutions: IResponseWithLoading<IrMap>;

    public resolutionModes: IrMap<ResolutionMode, string>;

    private subscription: Subscription;

    constructor(
        private readonly $scope: IScope,
        private readonly equipmentFunctionalityTypesRepository: IEquipmentFunctionalityTypesRepository,
    ) {}

    public $onInit(): void {
        this.originalEquipmentFunctionalityType = this.resolve.formData;
        this.equipmentFunctionalityType = cloneDeep(this.originalEquipmentFunctionalityType);

        this.subscription = new Subscription();

        this.subscription.add(
            extendWithLoadingAndFail(this.equipmentFunctionalityTypesRepository.getFpses())
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<number[]>) => {
                        this.fps = {
                            ...response,
                            data: response.data
                                ? response.data
                                      .sort()
                                      .reverse()
                                      .map((fps) => ({
                                          key: fps,
                                          value: FpsFunctions.GetFpsString(fps),
                                      }))
                                : undefined,
                        };
                        this.$scope.$applyAsync();
                    },
                    (error) => {
                        this.fps = error;
                    },
                ),
        );

        this.subscription.add(
            extendWithLoadingAndFail(this.equipmentFunctionalityTypesRepository.getResolutions())
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<string[]>) => {
                        this.resolutions = {
                            ...response,
                            data: response.data
                                ? response.data.reverse().map((resolution) => ({
                                      key: resolution,
                                      value: resolution,
                                  }))
                                : undefined,
                        };
                        this.$scope.$applyAsync();
                    },
                    (error) => {
                        this.resolutions = error;
                    },
                ),
        );

        this.resolutionModes = ResolutionModeFunctions.GetResolutionModesDictionary();
    }

    public $onDestroy(): void {
        this.subscription.unsubscribe();
    }

    public editVideoProperties(): void {
        const patchObject = {} as IEquipmentFunctionalityType;

        if (
            !isEqual(
                this.originalEquipmentFunctionalityType.supportedFpses.sort(),
                this.equipmentFunctionalityType.supportedFpses.sort(),
            )
        ) {
            patchObject.supportedFpses = this.equipmentFunctionalityType.supportedFpses;
        }

        if (
            !isEqual(
                this.originalEquipmentFunctionalityType.supportedResolutions.sort(),
                this.equipmentFunctionalityType.supportedResolutions.sort(),
            )
        ) {
            patchObject.supportedResolutions = this.equipmentFunctionalityType.supportedResolutions;
        }

        if (
            !isEqual(
                this.originalEquipmentFunctionalityType.supportedResolutionModes.sort(),
                this.equipmentFunctionalityType.supportedResolutionModes.sort(),
            )
        ) {
            patchObject.supportedResolutionModes = this.equipmentFunctionalityType.supportedResolutionModes;
        }

        if (!isEmpty(patchObject)) {
            this.subscription.add(
                extendWithLoadingAndFail(
                    this.equipmentFunctionalityTypesRepository.patchEquipmentFunctionalityType(
                        this.equipmentFunctionalityType.id,
                        patchObject,
                    ),
                )
                    .pipe(finalize(() => this.$scope.$applyAsync()))
                    .subscribe(
                        (editResponse: IResponseWithLoading<IEquipmentFunctionalityType>) => {
                            this.editResponse = editResponse;
                            if (!editResponse.isLoading && editResponse.data) {
                                this.resolve.options.onOk(editResponse.data);
                            }
                            this.$scope.$applyAsync();
                        },
                        (errorResponse) => {
                            this.editResponse = errorResponse;
                        },
                    ),
            );
        } else {
            this.cancel();
        }
    }

    public cancel(): void {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }
}
